import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { useAccountInfoSettings } from '../../account-info/hooks/useAccountInfoSettings';
import { useDemographicsPerson } from '../../account-info/hooks/useDemographicsPerson';
import { AccountInfoRoutes } from '../../account-info/constants/routes';
import { PersonalInformationApp } from '../../account-info/features/personal-info/personal-info';
import { MyRelationshipsApp } from '../../account-info/features/relationships/my-relationships';
import { MaritalStatusApp } from '../../account-info/features/marital-status/marital-status';
import { DependentInfo } from '../../account-info/features/dependents/dependent-info';
import { withLayout } from '../../../global/components/HOCs/with-layout';
import { withForbidden } from '../../../global/components/HOCs/with-forbidden';
import { TcsmFeatures } from '../../../global/models/features-model';
import AccountInfoContainer from '../../account-info/components/account-info-container';
import { AnnualEnrollmentContext, AnnualEnrollmentKey, AnnualEnrollmentState, ProcessingStatus } from '../models/annual-enrollment-models';
import { useAnnualEnrollmentSettings } from '../hooks/useAnnualEnrollmentSettings';
import { useAnnualEnrollmentInfo } from '../hooks/useAnnualEnrollmentInfo';
import { AnnualEnrollmentCoverage } from './annual-enrollment-coverage';
import { TcsmStates } from '../../../root/tcsm-model';
import { YesNoIndicators } from '../../../global/models/api-model';
import { AnnualEnrollmentRoutes } from '../constants/annual-enrollment-routes';
import { AnnualEnrollmentConfirmation } from './annual-enrollment-confirmation';
import { GpsType, LayoutModels } from '../../../global/components/layout/layout-models';
import { useSpouseInformation } from '../../account-info/hooks/useSpouseInfo';
import { useAnnualEnrollmentFatalError } from '../hooks/useAnnualEnrollmentFatalError';
import { withScrollTop } from '../../../global/components/HOCs/with-scroll-Top';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { PageContainer } from '../../../global/components/layout/page-container';
import { MainContent } from '../../../global/components/layout/main-content-container';
import { useSystemState } from '../../../global/hooks/useSystemState';

const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.AE_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.AE_DEMOGRAPHIC)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.AE_MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(null, GpsType.AE_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));
const AECoverageWrapper = withScrollTop()(withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.AECoverage)(AccountInfoContainer)));
const AEConfirmationWrapper = withLayout(null, GpsType.AE_CONFIRMTION)(withForbidden(TcsmFeatures.AEConfirmation)(AccountInfoContainer));
const AEHealdWrapper = withLayout(null, GpsType.AE_HELD)(withForbidden(TcsmFeatures.AEConfirmation)(AccountInfoContainer));
const AEAdminHeladWrapper = withLayout(null, GpsType.AE_ADMIN_HELD)(withForbidden(TcsmFeatures.AEConfirmation)(AccountInfoContainer));

interface Props {
	personalInfoSettingId: string;
	relationshipsSettingId: string;
	annualEnrollmentSettingId: string;
	docUpload?: boolean;
}

const AnnualEnrollmentApp = (props: Props) => {
	const app = useContext(DemographicsContext);
	const routeMatch = useRouteMatch();
	const demographicsSettingsLoaded = useAccountInfoSettings(props.personalInfoSettingId, props.relationshipsSettingId);
	const annualEnrollmentSettingsLoaded = useAnnualEnrollmentSettings(props.annualEnrollmentSettingId);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const personLoaded = useDemographicsPerson(app);
	const spousesLoaded = useSpouseInformation(personGuid);
	const AELoaded = useAnnualEnrollmentInfo(app);
	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const fatalErrorMessage = useAnnualEnrollmentFatalError(app);
	const currentPath = window.location?.pathname;
	const heldInReviewPath = `${routeMatch.path}${AnnualEnrollmentRoutes.HeldInReview}`;
	const currentCycleState = useSystemState();
	const isArc = app === DemographicAppKeys.arc;

	const today = currentCycleState?.base_cycle_date ? new Date(currentCycleState.base_cycle_date) : new Date();

	function IsAnnualEnrollmentFeatureOff() {
		return (AEState?.AEInfo as any)?.api_response?.status === 'AE_NOT_ACTIVE' ||
			AEState?.AEInfo?.ae_status?.ae_available === YesNoIndicators.No;
	}

	function IsAnnualEnrollmentMemberNotOpened() {
		return app === DemographicAppKeys.myCPG && AEState?.AEInfo?.ae_status?.first_enrollment_allowed && today < new Date(AEState?.AEInfo?.ae_status?.first_enrollment_allowed);
	}

	function IsAnnualEnrollmentAdminNotOpened() {
		return app === DemographicAppKeys.arc && AEState?.AEInfo?.ae_status?.first_enrollment_allowed && today < new Date(AEState?.AEInfo?.ae_status?.first_enrollment_allowed);
	}

	function IsAnnualEnrollmentMemberAlreadyClosed() {
		return app === DemographicAppKeys.myCPG && AEState?.AEInfo?.ae_status?.last_enrollment_allowed && today > new Date(AEState?.AEInfo?.ae_status?.last_enrollment_allowed);
	}

	function IsAnnualEnrollmentAdminAlreadyClosed() {
		return app === DemographicAppKeys.arc && AEState?.AEInfo?.ae_status?.last_admin_change_allowed && today > new Date(AEState?.AEInfo?.ae_status?.last_admin_change_allowed);
	}

	if (!demographicsSettingsLoaded || !personLoaded || !spousesLoaded || !AELoaded || !annualEnrollmentSettingsLoaded) {
		return <LoadingCircle />;
	}

	if (annualEnrollmentSettings?.disableAnnualEnrollment) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentDeactivatedMessage} />
				</MainContent>
			</PageContainer>
		);
	}

	if (IsAnnualEnrollmentFeatureOff()) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentNotAvailableMessage || 'Annual Enrollment not available'} />
				</MainContent>
			</PageContainer>);
	}

	if (IsAnnualEnrollmentMemberNotOpened()) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentNotYetAvailableMemberMessage || 'Annual Enrollment not available'} />
				</MainContent>
			</PageContainer>);
	}

	if (IsAnnualEnrollmentAdminNotOpened()) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentNotYetAvailableMAPMessage || 'Annual Enrollment not available'} />
				</MainContent>
			</PageContainer>);
	}

	if (IsAnnualEnrollmentMemberAlreadyClosed()) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentClosedMemberMessage || 'Annual Enrollment not available'} />
				</MainContent>
			</PageContainer>);
	}

	if (IsAnnualEnrollmentAdminAlreadyClosed()) {
		return (
			<PageContainer>
				<MainContent>
					<WysiwygWithReactLink content={annualEnrollmentSettings?.annualEnrollmentClosedMAPMessage || 'Annual Enrollment not available'} />
				</MainContent>
			</PageContainer>);
	}

	if ((AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.InProgress &&
		(AEState.AEInfo?.form_record?.processing_substatus === ProcessingStatus.HeldForReview || AEState.AEInfo?.form_record?.processing_substatus === ProcessingStatus.Failed)) &&
		!currentPath.includes(AnnualEnrollmentRoutes.HeldInReview)) {
		return <Redirect to={heldInReviewPath} />;
	}


	if (fatalErrorMessage) {
		return <div dangerouslySetInnerHTML={{ __html: fatalErrorMessage }} />;
	}

	const renderPreSubmissionRoutes = () => {
		if (AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.Completed
			|| AEState.AEInfo?.form_record?.processing_status === ProcessingStatus.Processed) {
			return <Redirect to={`${routeMatch.path}${AnnualEnrollmentRoutes.Confirmation}`} />;
		}

		return (
			<>
				<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
					<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<PersonalInformationApp basePath={routeMatch.path} docUpload={props.docUpload} />
					</PersonalInfoWrapper>
				)} />
				<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
					<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MyRelationshipsApp basePath={routeMatch.path} />
					</RelationshipsWrapper>
				)} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
					<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<MaritalStatusApp basePath={routeMatch.path} skipDocUpload={!props.docUpload} />
					</MaritalStatusWrapper>
				)} />
				<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
					<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
						<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} skipDocUpload={!props.docUpload} />
					</DependentsWrapper>
				)} />

				<Route exact path={`${routeMatch.path}${AnnualEnrollmentRoutes.Coverage}`} render={() => (
					<AECoverageWrapper hideBreadcrumbs>
						<AnnualEnrollmentCoverage basePath={routeMatch.path} />
					</AECoverageWrapper>
				)} />
			</>
		);
	};

	function RenderHeldInReview() {
		if (isArc) {
			return (
				<AEAdminHeladWrapper hideBreadcrumbs>
					<AnnualEnrollmentConfirmation basePath={routeMatch.path} />
				</AEAdminHeladWrapper>
			);
		}

		return (
			<AEHealdWrapper hideBreadcrumbs>
				<AnnualEnrollmentConfirmation basePath={routeMatch.path} />
			</AEHealdWrapper>
		);
	}

	return (
		<>
			<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.annualEnrollment}>
				<Switch>
					<Route exact path={`${routeMatch.path}${AnnualEnrollmentRoutes.Confirmation}`} render={() => (
						<AEConfirmationWrapper hideBreadcrumbs>
							<AnnualEnrollmentConfirmation basePath={routeMatch.path} />
						</AEConfirmationWrapper>
					)} />

					<Route exact path={`${routeMatch.path}${AnnualEnrollmentRoutes.HeldInReview}`} render={() => (
						<RenderHeldInReview />
					)} />

					{renderPreSubmissionRoutes()}
				</Switch>
			</AnnualEnrollmentContext.Provider>
		</>
	);
};

AnnualEnrollmentApp.defaultProps = {
	docUpload: true,
};

export default AnnualEnrollmentApp;